import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSelector } from "react-redux";

// mui components
import { Box, Drawer, IconButton, Stack, Tooltip } from "@mui/material";

// mui icons
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";

// custom components
import Heading3 from "../../../typography/heading-03";
import Heading5 from "../../../typography/heading-05";
import Body2 from "../../../typography/body-02";
import PrimaryButton from "../../../buttons/primary-button";
import SecondaryButton from "../../../buttons/secondary-button";
import LoadingDataModal from "../../../modal/loading-data-modal";
import DocumentsTrackerDrawer from "../documents-tracker";
import EnhancedConditionLinkExistingDrawer from "../enhanced-condition-link-existing";
import { fetchLoanAttachmentDocuments } from "./supporting-documents";
import { selectEnhancedConditionStatus } from "../../../../features/enhanced-condition-status/enhancedConditionStatusSlice";
import { useEnhancedConditionsDocumentsListMutation } from "../../../../features/enhanced-conditions-documents_list/fetchEnhancedConditionsDocumentsListApiSlice";
import { useEnhancedConditionRemoveAttachmentMutation } from "../../../../features/enhanced-condition-remove-attachment/enhanced-condition-remove-attachment";
import { useGetAttachmentsMutation } from "../../../../features/get-attachments/getAttachmentsApiSlice";
import { waitForPendingApiRequestsToSettle } from "../../../../features/api-request-status/apiRequestStatusSlice";
import { store } from "../../../../app/store";

// custom styles
import styles from "./index.module.scss";

const normalizeText = (value) => String(value ?? "").trim();

const firstNonEmpty = (...values) => {
	for (const value of values) {
		if (Array.isArray(value)) {
			const joined = value.filter(Boolean).join(", ").trim();
			if (joined) return joined;
			continue;
		}

		const text = normalizeText(value);
		if (text) return text;
	}

	return "";
};

const isFalseLike = (value) =>
	value === false ||
	["false", "0"].includes(normalizeText(value).toLowerCase());

const formatDate = (value) => {
	if (!value) return "";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";

	return `${date.toLocaleDateString("en-US", {
		timeZone: "America/New_York",
		month: "short",
		day: "numeric",
		year: "numeric",
	})}, ${date.toLocaleTimeString("en-US", {
		timeZone: "America/New_York",
		hour: "numeric",
		minute: "2-digit",
	})} ET`;
};

const formatDateTime = (value) => {
	if (!value) return "NA";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "NA";

	return `${date.toLocaleString("en-US", {
		timeZone: "America/New_York",
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	})} ET`;
};

const humanizeValue = (value) => {
	const text = normalizeText(value);
	if (!text) return "NA";

	return text
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
};

const getStatusMeta = (status) => {
	const normalized = normalizeText(status).toLowerCase();

	if (normalized === "open") {
		return {
			key: "open",
			label: "Open",
			title: "Action Needed",
			message: "This condition is still open and requires documentation.",
		};
	}

	if (normalized === "requested") {
		return {
			key: "requested",
			label: "Requested",
			title: "Awaiting Documentation",
			message: "We are waiting on the requested documents.",
		};
	}

	if (normalized === "added") {
		return {
			key: "added",
			label: "Added",
			title: "Condition Added",
			message: "This condition has been added.",
		};
	}

	if (normalized === "fulfilled") {
		return {
			key: "fulfilled",
			label: "Fulfilled",
			title: "Condition Satisfied",
			message: "This condition has been fulfilled.",
		};
	}

	if (normalized === "waived") {
		return {
			key: "waived",
			label: "Waived",
			title: "Condition Waived",
			message: "This condition has been waived.",
		};
	}

	return {
		key: "neutral",
		label: normalizeText(status) || "Unknown",
		title: "Condition Update",
		message: "Condition details are available.",
	};
};

const getDocumentStatusTone = (value) => {
	const normalized = normalizeText(value).toLowerCase();

	if (
		normalized.includes("fulfill") ||
		normalized.includes("received") ||
		normalized.includes("linked") ||
		normalized.includes("uploaded")
	) {
		return "success";
	}

	if (normalized.includes("pending") || normalized.includes("open")) {
		return "danger";
	}

	if (normalized.includes("request")) {
		return "warning";
	}

	return "neutral";
};

const normalizeDocumentItem = (item, fallbackUploadDate) => ({
	id:
		item?.id ?? item?.entityId ?? item?.documentId ?? item?.name ?? item?.title,
	fileName: firstNonEmpty(
		item?.fileName,
		item?.entityName,
		item?.name,
		item?.title,
		item?.documentName,
		item?.label,
		"Untitled Document"
	),
	status: firstNonEmpty(
		item?.loganConnectStatus,
		item?.documentStatus,
		item?.status,
		item?.connectionStatus,
		item?.assignedStatus,
		"Linked"
	),
	uploadDate: firstNonEmpty(
		item?.uploadDate,
		item?.createdDate,
		item?.statusDate,
		fallbackUploadDate
	),
});

const getConditionIdentifier = (value) =>
	typeof value === "string" || typeof value === "number"
		? firstNonEmpty(value)
		: firstNonEmpty(
				value?.conditionId,
				value?.entityId,
				value?.id,
				value?.value,
				value?.label
			);

const getDocumentConditionIds = (document) =>
	[
		...(Array.isArray(document?.conditions) ? document.conditions : []),
		document?.conditionId,
		document?.condition_id,
		document?.assignedTo?.entityId,
		document?.assignedTo?.conditionId,
		...(Array.isArray(document?.assignedTo)
			? document.assignedTo.map((item) => item?.entityId ?? item?.conditionId)
			: []),
	]
		.map(getConditionIdentifier)
		.filter(Boolean);

const doesDocumentMatchConditionId = (document, conditionId) => {
	if (!conditionId) return false;

	return getDocumentConditionIds(document).some(
		(documentConditionId) => String(documentConditionId) === String(conditionId)
	);
};

const getAttachmentIdentifier = (attachment) =>
	firstNonEmpty(
		attachment?.id,
		attachment?.entityId,
		attachment?.attachmentId,
		attachment?.documentId,
		attachment?.name,
		attachment?.title
	);

const normalizeAttachmentDocument = (
	attachment,
	parentDocument,
	fallbackUploadDate
) =>
	normalizeDocumentItem(
		{
			...attachment,
			id: getAttachmentIdentifier(attachment),
			title: firstNonEmpty(
				attachment?.entityName,
				attachment?.title,
				attachment?.fileName,
				attachment?.name,
				parentDocument?.title,
				"Untitled Document"
			),
			fileName: firstNonEmpty(
				attachment?.entityName,
				attachment?.fileName,
				attachment?.title,
				attachment?.name,
				parentDocument?.title,
				"Untitled Document"
			),
			status: firstNonEmpty(
				attachment?.loganConnectStatus,
				attachment?.documentStatus,
				attachment?.status,
				parentDocument?.loganConnectStatus,
				parentDocument?.documentStatus,
				parentDocument?.status,
				"Linked"
			),
			uploadDate: firstNonEmpty(
				attachment?.uploadDate,
				attachment?.createdDate,
				attachment?.statusDate,
				parentDocument?.lastAttachmentDate,
				parentDocument?.uploadDate,
				parentDocument?.statusDate,
				parentDocument?.updatedDate,
				fallbackUploadDate
			),
			raw: attachment,
			parentDocument,
		},
		fallbackUploadDate
	);

const dedupeDocuments = (documents) => {
	const seen = new Map();

	documents.forEach((document) => {
		const key = String(
			document?.id ??
				document?.entityId ??
				document?.documentId ??
				document?.fileName ??
				document?.uploadDate ??
				""
		);

		if (!key) return;
		seen.set(key, document);
	});

	return Array.from(seen.values());
};

const getRowDocumentEntityIds = (row) => {
	if (Array.isArray(row?.documents)) {
		return row.documents
			.map((document) =>
				String(document?.entityId ?? document?.id ?? "").trim()
			)
			.filter(Boolean);
	}

	if (Array.isArray(row?.documents?.entityIds)) {
		return row.documents.entityIds
			.map((entityId) => String(entityId).trim())
			.filter(Boolean);
	}

	if (
		row?.documents?.entityIds === undefined ||
		row?.documents?.entityIds === null
	) {
		return [];
	}

	const normalizedEntityId = String(row.documents.entityIds).trim();
	return normalizedEntityId ? [normalizedEntityId] : [];
};

const getLoanAttachmentEntityId = (document) =>
	String(
		document?.assignedTo?.entityId ??
			document?.assignedTo?.id ??
			document?.assignedTo?.entityID ??
			document?.entityId ??
			document?.id ??
			document?.documentId ??
			document?.attachmentId ??
			""
	).trim();

const getDirectSupportingDocuments = (row) =>
	Array.isArray(row?.supportingDocuments)
		? row.supportingDocuments
		: Array.isArray(row?.linkedDocuments)
			? row.linkedDocuments
			: Array.isArray(row?.documents)
				? row.documents
				: Array.isArray(row?.attachments)
					? row.attachments
					: [];

const getEnhancedSupportingDocuments = ({
	row,
	conditionId,
	fallbackUploadDate,
	loanAttachmentDocuments,
}) => {
	const directSupportingDocuments = getDirectSupportingDocuments(row);
	const hasFetchedLoanAttachmentDocuments = Array.isArray(
		loanAttachmentDocuments
	)
		? loanAttachmentDocuments.length > 0
		: false;

	if (hasFetchedLoanAttachmentDocuments) {
		const resolvedDocuments = [];

		(loanAttachmentDocuments || []).forEach((document) => {
			const documentMatches = doesDocumentMatchConditionId(
				document,
				conditionId
			);
			const attachments = Array.isArray(document?.attachments)
				? document.attachments
				: [];

			if (documentMatches) {
				resolvedDocuments.push(
					normalizeAttachmentDocument(
						document,
						document,
						fallbackUploadDate || row?.createdDate || ""
					)
				);
			}

			attachments.forEach((attachment) => {
				const attachmentMatches =
					documentMatches ||
					doesDocumentMatchConditionId(attachment, conditionId);

				if (!attachmentMatches) {
					return;
				}

				resolvedDocuments.push(
					normalizeAttachmentDocument(
						attachment,
						document,
						fallbackUploadDate || row?.createdDate || ""
					)
				);
			});
		});

		return dedupeDocuments(resolvedDocuments);
	}

	return dedupeDocuments(
		directSupportingDocuments
			.map((document) =>
				normalizeAttachmentDocument(
					document,
					row,
					fallbackUploadDate || row?.createdDate || ""
				)
			)
			.filter(Boolean)
	);
};

const getStandardSupportingDocuments = ({
	row,
	fallbackUploadDate,
	loanAttachmentDocuments,
	rowDocumentEntityIds,
}) => {
	const matchingDocuments = (loanAttachmentDocuments || []).filter(
		(document) => {
			const documentId = getLoanAttachmentEntityId(document);

			return (
				documentId &&
				rowDocumentEntityIds.some((entityId) => String(entityId) === documentId)
			);
		}
	);

	return dedupeDocuments(
		matchingDocuments
			.map((document) =>
				normalizeDocumentItem(
					document,
					fallbackUploadDate || row?.createdDate || ""
				)
			)
			.filter(Boolean)
	);
};

const getSupportingDocumentsFromLoan = ({
	isEnhancedEnabled,
	row,
	conditionId,
	fallbackUploadDate,
	loanAttachmentDocuments,
	rowDocumentEntityIds,
}) =>
	isEnhancedEnabled
		? getEnhancedSupportingDocuments({
				row,
				conditionId,
				fallbackUploadDate,
				loanAttachmentDocuments,
			})
		: getStandardSupportingDocuments({
				row,
				fallbackUploadDate,
				loanAttachmentDocuments,
				rowDocumentEntityIds,
			});

const EnhancedConditionDetailDrawer = ({
	handleCloseDrawer,
	handleOpenProcessingModal,
	handleCloseProcessingModal,
	handleOpenSuccessModal,
	setProcessingText,
	setSuccessText,
	handleOpenCommonErrorModal,
	drawerData = {},
}) => {
	const [removeEnhancedConditionAttachment] =
		useEnhancedConditionRemoveAttachmentMutation();
	const [loadLoanAttachmentDocuments] =
		useEnhancedConditionsDocumentsListMutation();
	const [getAttachments] = useGetAttachmentsMutation();
	const enhancedConditionsStatusFromStore = useSelector(
		selectEnhancedConditionStatus
	);
	const enhancedConditionsStatus = String(
		enhancedConditionsStatusFromStore ?? ""
	)
		.trim()
		.toUpperCase();
	const isEnhancedEnabled = enhancedConditionsStatus === "Y";
	const row = useMemo(
		() => {
			const baseRow =
				drawerData?.selectedDocument ??
				drawerData?.row ??
				drawerData?.document ??
				drawerData ??
				{};

			if (!isEnhancedEnabled) {
				return baseRow;
			}

			const drawerSupportingDocuments = Array.isArray(
				drawerData?.supportingDocuments
			)
				? drawerData.supportingDocuments
				: [];

			if (drawerSupportingDocuments.length === 0) {
				return baseRow;
			}

			const baseSupportingDocuments = Array.isArray(baseRow?.supportingDocuments)
				? baseRow.supportingDocuments
				: [];
			const mergedSupportingDocuments =
				baseSupportingDocuments.length > 0
					? baseSupportingDocuments
					: drawerSupportingDocuments;
			const baseDocuments = Array.isArray(baseRow?.documents)
				? baseRow.documents
				: [];
			const baseLinkedDocuments = Array.isArray(baseRow?.linkedDocuments)
				? baseRow.linkedDocuments
				: [];
			const drawerCountOfDoc = Number(drawerData?.count_of_doc);
			const baseCountOfDoc = Number(baseRow?.count_of_doc);

			return {
				...baseRow,
				supportingDocuments: mergedSupportingDocuments,
				documents: baseDocuments.length > 0 ? baseDocuments : mergedSupportingDocuments,
				linkedDocuments:
					baseLinkedDocuments.length > 0
						? baseLinkedDocuments
						: mergedSupportingDocuments,
				count_of_doc:
					Number.isFinite(baseCountOfDoc) && baseCountOfDoc > 0
						? baseCountOfDoc
						: Number.isFinite(drawerCountOfDoc) && drawerCountOfDoc > 0
							? drawerCountOfDoc
							: mergedSupportingDocuments.length,
			};
		},
		[drawerData, isEnhancedEnabled]
	);
	const conditionId = firstNonEmpty(row?.id, row?.entityId, row?.conditionId);
	const resolvedLoanId = firstNonEmpty(
		drawerData?.loanId,
		drawerData?.loanGuid,
		drawerData?.loan_guid,
		row?.loanId,
		row?.loanGuid,
		row?.loan_guid
	);

	const statusMeta = getStatusMeta(row?.sub_status ?? row?.status);
	const statusKeyClass =
		statusMeta.key.charAt(0).toUpperCase() + statusMeta.key.slice(1);
	const isClearedCondition = ["cleared", "rejected"].includes(
		normalizeText(row?.sub_status).toLowerCase()
	);
	const allowToClear =
		drawerData?.allowToClear ?? row?.allowToClear ?? row?.allow_to_clear;
	const canShowActions =
		!isFalseLike(allowToClear) &&
		(statusMeta.key === "open" || statusMeta.key === "requested");
	const isUnlinkButtonDisabled = isClearedCondition;
	const fallbackUploadDate = useMemo(
		() =>
			firstNonEmpty(
				row?.fulfilledDate,
				row?.statusDate,
				row?.createdDate,
				row?.updatedDate,
				row?.lastModifiedDate
			),
		[
			row?.createdDate,
			row?.fulfilledDate,
			row?.lastModifiedDate,
			row?.statusDate,
			row?.updatedDate,
		]
	);
	const [isUploadOpen, setIsUploadOpen] = useState(false);
	const [isLinkExistingOpen, setIsLinkExistingOpen] = useState(false);
	const [supportingDocuments, setSupportingDocuments] = useState([]);
	const [isSupportingDocumentsLoading, setIsSupportingDocumentsLoading] =
		useState(false);
	const [isProcessingModalVisible, setIsProcessingModalVisible] =
		useState(false);
	const [supportingDocumentsError, setSupportingDocumentsError] = useState("");
	const initialLoanAttachmentDocuments = useMemo(
		() =>
			Array.isArray(drawerData?.loanAttachmentDocuments)
				? drawerData.loanAttachmentDocuments
				: [],
		[drawerData?.loanAttachmentDocuments]
	);
	const [loanAttachmentDocuments, setLoanAttachmentDocuments] = useState(
		initialLoanAttachmentDocuments
	);
	const loanAttachmentDocumentsRef = useRef(initialLoanAttachmentDocuments);
	const conditionTitle = firstNonEmpty(
		row?.title,
		row?.conditionTitle,
		drawerData?.conditionTitle
	);
	const descriptionText = normalizeText(
		isEnhancedEnabled ? row?.externalDescription : row?.description
	);
	const onSupportingDocumentsRefresh = drawerData?.onSupportingDocumentsRefresh;
	const onLoanAttachmentDocumentsRefresh =
		drawerData?.onLoanAttachmentDocumentsRefresh;

	useEffect(() => {
		loanAttachmentDocumentsRef.current = initialLoanAttachmentDocuments;
		setLoanAttachmentDocuments(initialLoanAttachmentDocuments);
	}, [initialLoanAttachmentDocuments]);

	const rowDocumentEntityIds = useMemo(() => {
		return getRowDocumentEntityIds(row);
	}, [row]);

	const enhancedSupportingDocuments = useMemo(() => {
		if (!isEnhancedEnabled) {
			return [];
		}

		const drawerSupportingDocuments = Array.isArray(
			drawerData?.supportingDocuments
		)
			? drawerData.supportingDocuments
			: [];
		const rowSupportingDocuments = Array.isArray(row?.supportingDocuments)
			? row.supportingDocuments
			: [];
		const rowLinkedDocuments = Array.isArray(row?.linkedDocuments)
			? row.linkedDocuments
			: [];

		return dedupeDocuments([
			...drawerSupportingDocuments,
			...rowSupportingDocuments,
			...rowLinkedDocuments,
		]);
	}, [
		drawerData?.supportingDocuments,
		isEnhancedEnabled,
		row?.linkedDocuments,
		row?.supportingDocuments,
	]);

	const supportingDocumentsFromLoan = useMemo(() => {
		if (isEnhancedEnabled && enhancedSupportingDocuments.length > 0) {
			return enhancedSupportingDocuments;
		}

		return getSupportingDocumentsFromLoan({
			isEnhancedEnabled,
			row,
			conditionId,
			conditionTitle,
			fallbackUploadDate,
			loanAttachmentDocuments,
			rowDocumentEntityIds,
		});
	}, [
		conditionId,
		fallbackUploadDate,
		isEnhancedEnabled,
		enhancedSupportingDocuments,
		loanAttachmentDocuments,
		conditionTitle,
		row,
		rowDocumentEntityIds,
	]);

	const getSupportingDocumentDisplayName = useCallback(
		(document) =>
			isEnhancedEnabled
				? firstNonEmpty(
						document?.title,
						document?.fileName,
						document?.name,
						"Untitled Document"
					)
				: firstNonEmpty(
						document?.fileName,
						document?.title,
						document?.name,
						"Untitled Document"
					),
		[isEnhancedEnabled]
	);

	const getSupportingDocumentDateValue = useCallback(
		(document) =>
			isEnhancedEnabled ? document?.createdDate : document?.uploadDate,
		[isEnhancedEnabled]
	);

	const getSupportingDocumentStatusValue = useCallback(
		(document) =>
			firstNonEmpty(
				document?.status,
				document?.loganConnectStatus,
				document?.documentStatus,
				document?.connectionStatus,
				document?.assignedStatus,
				"Linked"
			),
		[]
	);

	const supportingDocumentsCount = isEnhancedEnabled
		? (() => {
				const rowCount = Number(row?.count_of_doc);
				const drawerCount = Number(drawerData?.count_of_doc);
				const resolvedCount = Array.isArray(supportingDocumentsFromLoan)
					? supportingDocumentsFromLoan.length
					: 0;

				if (Number.isFinite(rowCount) && rowCount > 0) {
					return rowCount;
				}

				if (Number.isFinite(drawerCount) && drawerCount > 0) {
					return drawerCount;
				}

				return resolvedCount;
			})()
		: supportingDocuments.length;

	useEffect(() => {
		if (!isEnhancedEnabled) {
			return;
		}

	}, [
		conditionTitle,
		drawerData?.count_of_doc,
		isEnhancedEnabled,
		row,
		supportingDocumentsFromLoan,
	]);

	useEffect(() => {
		setSupportingDocumentsError("");
		setSupportingDocuments([]);
		setIsSupportingDocumentsLoading(
			Boolean(resolvedLoanId && conditionId) &&
				loanAttachmentDocumentsRef.current.length === 0
		);
	}, [conditionId, resolvedLoanId]);

	useEffect(() => {
		const nextSupportingDocuments =
			isEnhancedEnabled && enhancedSupportingDocuments.length > 0
				? enhancedSupportingDocuments
				: supportingDocumentsFromLoan;

		setSupportingDocuments(nextSupportingDocuments);
		onSupportingDocumentsRefresh?.(nextSupportingDocuments);
	}, [
		enhancedSupportingDocuments,
		isEnhancedEnabled,
		onSupportingDocumentsRefresh,
		supportingDocumentsFromLoan,
	]);

	const refreshSupportingDocumentsFromLoan = useCallback(
		async ({ forceReload = false } = {}) => {
			if (!resolvedLoanId || !conditionId) {
				setSupportingDocuments([]);
				setSupportingDocumentsError("");
				return [];
			}

			const cachedLoanAttachmentDocuments = Array.isArray(
				loanAttachmentDocumentsRef.current
			)
				? loanAttachmentDocumentsRef.current
				: [];

			if (!forceReload && cachedLoanAttachmentDocuments.length > 0) {
				return cachedLoanAttachmentDocuments;
			}

			setIsSupportingDocumentsLoading(true);
			setSupportingDocumentsError("");

			try {
				const loanDocuments = await fetchLoanAttachmentDocuments({
					loanId: resolvedLoanId,
					isEnhancedEnabled,
					loadLoanAttachmentDocuments,
					getAttachments,
				});

				loanAttachmentDocumentsRef.current = loanDocuments;
				setLoanAttachmentDocuments(loanDocuments);
				onLoanAttachmentDocumentsRefresh?.(loanDocuments);

				return loanDocuments;
			} catch (error) {
				loanAttachmentDocumentsRef.current = [];
				setLoanAttachmentDocuments([]);
				setSupportingDocuments([]);
				setSupportingDocumentsError(
					"Unable to load supporting documents for the selected condition."
				);
				handleOpenCommonErrorModal?.(
					"ENHANCED_CONDITION_DOCUMENTS_UNAVAILABLE",
					error
				);
				throw error;
			} finally {
				setIsSupportingDocumentsLoading(false);
			}
		},
		[
			conditionId,
			handleOpenCommonErrorModal,
			isEnhancedEnabled,
			getAttachments,
			loadLoanAttachmentDocuments,
			onLoanAttachmentDocumentsRefresh,
			resolvedLoanId,
		]
	);

	const openProcessingModal = useCallback(() => {
		setIsProcessingModalVisible(true);
		handleOpenProcessingModal?.();
	}, [handleOpenProcessingModal]);

	const closeProcessingModal = useCallback(() => {
		setIsProcessingModalVisible(false);
		handleCloseProcessingModal?.();
	}, [handleCloseProcessingModal]);

	useEffect(() => {
		if (!resolvedLoanId || !conditionId) {
			return;
		}

		void refreshSupportingDocumentsFromLoan({ forceReload: true }).catch(
			() => {}
		);
	}, [conditionId, refreshSupportingDocumentsFromLoan, resolvedLoanId]);

	const metadataItems = [
		{ label: "Condition Name", value: firstNonEmpty(row?.title) },
		{ label: "Category", value: firstNonEmpty(row?.category) },
		{ label: "Status", value: row?.sub_status },
		{ label: "Prior To", value: firstNonEmpty(row?.priorTo) },
		{
			label: "Source",
			value: humanizeValue(row?.source ? row?.source : "-"),
		},
		{
			label: "Date Added",
			value: formatDateTime(row?.createdDate ?? row?.statusDate),
		},
		{
			label: "Fulfilled / Waived Date",
			value: formatDate(
				row?.fulfilledDate ?? row?.fulfilledOn ?? row?.waivedDate
			),
		},
		{ label: "Condition ID", value: conditionId || "NA" },
	];

	const selectedConditionOption = useMemo(
		() => ({
			id: conditionId || row?.id || row?.entityId || row?.title,
			title: firstNonEmpty(row?.title, row?.conditionTitle, "Condition"),
			label: firstNonEmpty(row?.title, row?.conditionTitle, "Condition"),
			name: firstNonEmpty(row?.title, row?.conditionTitle, "Condition"),
			entityName: firstNonEmpty(row?.title, row?.conditionTitle, "Condition"),
			conditionType: firstNonEmpty(row?.conditionType, row?.condition_type),
			category: row?.category ?? "",
			description:
				row?.description ??
				row?.documentDescription ??
				row?.internalDescription ??
				row?.externalDescription ??
				"",
			internalDescription:
				row?.internalDescription ?? row?.externalDescription ?? "",
		}),
		[conditionId, row]
	);

	const openUploadModal = () => {
		setIsUploadOpen(true);
	};

	const closeUploadModal = () => {
		setIsUploadOpen(false);
	};

	const openLinkExistingModal = () => {
		const onLinkExisting =
			typeof drawerData?.onLinkExisting === "function"
				? drawerData.onLinkExisting
				: null;

		const shouldOpenLinkDrawer = onLinkExisting
			? onLinkExisting({
					conditionId,
					condition: row,
				})
			: true;

		if (shouldOpenLinkDrawer === false) {
			return;
		}

		setIsLinkExistingOpen(true);
	};

	const closeLinkExistingModal = () => {
		setIsLinkExistingOpen(false);
	};

	const handleUploadAndLink = () => {
		const onUploadAndLink =
			typeof drawerData?.onUploadAndLink === "function"
				? drawerData.onUploadAndLink
				: null;

		const shouldOpenUploadDrawer = onUploadAndLink
			? onUploadAndLink({
					conditionId,
					condition: row,
				})
			: true;

		if (shouldOpenUploadDrawer === false) {
			return;
		}

		openUploadModal();
	};

	const handleUploadSuccess = async ({
		fileCount = 0,
		files = [],
		conditionId: uploadedConditionId,
	}) => {
		await drawerData?.onUploadSuccess?.({
			fileCount,
			conditionId: uploadedConditionId ?? conditionId,
			files,
		});

		await refreshSupportingDocumentsFromLoan({ forceReload: true });
		handleCloseDrawer?.();
		await drawerData?.onConditionListRefresh?.();
		await waitForPendingApiRequestsToSettle(store);
	};

	const handleLinkExistingSuccess = async ({
		conditionId: linkedConditionId,
		documents = [],
	}) => {
		await drawerData?.onLinkExistingSuccess?.({
			conditionId: linkedConditionId ?? conditionId,
			documents,
		});

		await refreshSupportingDocumentsFromLoan({ forceReload: true });
		handleCloseDrawer?.();
		await drawerData?.onConditionListRefresh?.();
		await waitForPendingApiRequestsToSettle(store);
	};

	const handleUnlinkDocument = async (document) => {
		const documentId = document?.id;
		if (!documentId || isUnlinkButtonDisabled) return;

		openProcessingModal();
		setProcessingText?.("Unlinking document from condition");

		try {
			const response = await removeEnhancedConditionAttachment({
				loanGuid: resolvedLoanId,
				data: [
					{
						id: documentId,
					},
				],
			}).unwrap();

			if (response?.status && response.status !== "success") {
				throw response;
			}

			// const remainingDocuments = supportingDocuments.filter(
			// 	(item) => String(item?.id) !== String(documentId)
			// );

			// setSupportingDocuments(remainingDocuments);

			await drawerData?.onUnlinkSuccess?.({
				conditionId,
				documentId,
				document,
			});

			await refreshSupportingDocumentsFromLoan({ forceReload: true });
		} catch (error) {
			handleOpenCommonErrorModal?.(
				"ENHANCED_CONDITION_REMOVE_CONDITION_FAILED",
				error
			);
		} finally {
			closeProcessingModal();
		}
	};

	return (
		<>
			<LoadingDataModal
				open={isSupportingDocumentsLoading && !isProcessingModalVisible}
				text="Loading supporting documents..."
			/>

			<Stack direction="column" className={styles.drawerRoot}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
				>
					<Stack direction="column" gap={1.25} className={styles.headerCopy}>
						<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
							{conditionId ? (
								<Box
									className={`${styles.headerPill} ${styles.headerPillMuted}`}
								>
									<Body2
										text={conditionId}
										extraClass={styles.headerPillText}
									/>
								</Box>
							) : null}
							<Box
								className={`${styles.headerPill} ${
									styles[`headerPill${statusKeyClass}`] ||
									styles.headerPillNeutral
								}`}
							>
								<Body2
									text={statusMeta.label}
									extraClass={styles.headerPillText}
								/>
							</Box>
						</Stack>

						<Heading3
							text={firstNonEmpty(row?.title, "Condition Detail")}
							extraClass={styles.drawerTitle}
							fontType="semibold"
						/>
					</Stack>

					<IconButton
						onClick={handleCloseDrawer}
						aria-label="Close enhanced condition detail"
						className={styles.closeButton}
					>
						<CloseIcon className={styles.closeIcon} />
					</IconButton>
				</Stack>

				<Box
					className={`${styles.summaryBanner} ${
						styles[`summaryBanner${statusKeyClass}`] ||
						styles.summaryBannerNeutral
					}`}
				>
					<Box className={styles.summaryIconWrap}>
						<ErrorOutlineOutlinedIcon className={styles.summaryIcon} />
					</Box>
					<Stack direction="column" gap={0.2} className={styles.summaryCopy}>
						<Body2 text={statusMeta.title} extraClass={styles.summaryTitle} />
						<Body2
							text={statusMeta.message}
							extraClass={styles.summaryMessage}
						/>
					</Stack>
				</Box>

				{descriptionText ? (
					<Box className={styles.sectionBlock}>
						<Heading5
							text="CONDITION DESCRIPTION"
							fontType="semibold"
							extraClass={styles.sectionTitle}
						/>
						<Box className={styles.notesCallout}>
							<Box className={styles.notesHeaderRow}>
								<ChatBubbleOutlineOutlinedIcon className={styles.notesIcon} />
								<Body2
									text="Condition Description"
									extraClass={styles.notesHeader}
								/>
							</Box>
							<Body2 text={descriptionText} extraClass={styles.notesText} />
						</Box>
					</Box>
				) : null}

				<Box className={styles.sectionBlock}>
					<Heading5
						text="CONDITION DETAILS"
						fontType="semibold"
						extraClass={styles.sectionTitle}
					/>
					<Box className={styles.metadataCard}>
						<Box className={styles.metadataList}>
							{metadataItems.map((item) => (
								<Box key={item.label} className={styles.metadataRow}>
									<Body2 text={item.label} extraClass={styles.metadataLabel} />
									{item.label === "Condition ID" ? (
										<Box className={styles.inlinePillMuted}>
											<Body2
												text={item.value || "NA"}
												extraClass={styles.inlinePillText}
											/>
										</Box>
									) : item.label === "Category" ? (
										<Box className={styles.inlinePillMuted}>
											<Body2
												text={item.value || "NA"}
												extraClass={styles.inlinePillText}
											/>
										</Box>
									) : item.label === "Status" ? (
										<Box
											className={`${styles.headerPill} ${
												styles[`headerPill${statusKeyClass}`] ||
												styles.headerPillNeutral
											}`}
										>
											<Body2
												text={item.value || "NA"}
												extraClass={styles.headerPillText}
											/>
										</Box>
									) : (
										<Body2
											text={item.value || "-"}
											extraClass={styles.metadataValue}
										/>
									)}
								</Box>
							))}
						</Box>
					</Box>
				</Box>

				<Box className={styles.sectionBlock}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={2}
						className={styles.documentsHeader}
					>
						<Heading5
							text={`SUPPORTING DOCUMENTS (${supportingDocumentsCount})`}
							fontType="semibold"
							extraClass={styles.sectionTitle}
						/>
						<Stack
							direction="row"
							alignItems="center"
							gap={1.25}
							className={styles.actionRow}
						>
							<PrimaryButton
								text="Upload & Link"
								onClick={handleUploadAndLink}
								disabled={isClearedCondition}
							/>
							<SecondaryButton
								text="Link Existing"
								onClick={openLinkExistingModal}
								disabled={isClearedCondition}
							/>
						</Stack>
					</Stack>

					{supportingDocumentsError ? (
						<Box className={styles.emptyState}>
							<ErrorOutlineOutlinedIcon className={styles.emptyStateIcon} />
							<Body2
								text={supportingDocumentsError}
								extraClass={styles.emptyStateText}
							/>
						</Box>
					) : supportingDocuments.length > 0 ? (
						<Box className={styles.documentsCard}>
							<Box className={styles.documentsTableScroll}>
								<Box className={styles.documentsHeaderRow}>
									<Body2
										text="File Name"
										extraClass={styles.documentsHeaderCell}
									/>
									<Body2
										text="Logan Connect Status"
										extraClass={styles.documentsHeaderCell}
									/>
									<Body2
										text="Upload Date"
										extraClass={styles.documentsHeaderCell}
									/>
									<Body2 text="" extraClass={styles.documentsHeaderCell} />
								</Box>

								{supportingDocuments.map((document, index) => {
									const documentStatus =
										getSupportingDocumentStatusValue(document);
									const statusTone = getDocumentStatusTone(documentStatus);
									const displayName =
										getSupportingDocumentDisplayName(document);
									return (
										<Box
											key={`${document.id ?? displayName ?? "doc"}-${index}`}
											className={styles.documentRow}
										>
											<Box className={styles.documentFileCell}>
												<Body2
													text={displayName}
													extraClass={styles.documentFileName}
												/>
											</Box>

											<Box className={styles.documentStatusCell}>
												<Box
													className={`${styles.documentStatusBadge} ${
														styles[
															`documentStatusBadge${statusTone.charAt(0).toUpperCase()}${statusTone.slice(1)}`
														]
													}`}
												>
													<Body2
														text={documentStatus}
														extraClass={styles.documentStatusText}
													/>
												</Box>
											</Box>

											<Box className={styles.documentDateCell}>
												<Body2
													text={formatDateTime(
														getSupportingDocumentDateValue(document)
													)}
													extraClass={styles.documentDate}
												/>
											</Box>

											{/* <Box className={styles.documentActionCell}>
												{canShowActions || !isClearedCondition ? (
													<Tooltip
														title="Unlink document"
														arrow
														placement="top"
													>
													<IconButton
														className={styles.unlinkButton}
														aria-label={`Unlink ${displayName}`}
														onClick={() => handleUnlinkDocument(document)}
														disabled={isUnlinkButtonDisabled}
													>
															<LinkOffOutlinedIcon
																className={styles.unlinkIcon}
															/>
														</IconButton>
													</Tooltip>
												) : null}
											</Box> */}
										</Box>
									);
								})}
							</Box>
						</Box>
					) : (
						<Box className={styles.emptyState}>
							<DescriptionOutlinedIcon className={styles.emptyStateIcon} />
							<Body2
								text="No Documents Linked"
								extraClass={styles.emptyStateText}
							/>
						</Box>
					)}
				</Box>

				<Drawer
					anchor="right"
					open={isUploadOpen}
					onClose={closeUploadModal}
					PaperProps={{
						sx: {
							height: "75vh",
							minHeight: "75vh",
							maxHeight: "75vh",
							overflow: "hidden",
							display: "flex",
							width: { xs: "100%", md: "56vw" },
							minWidth: { md: "56vw" },
							maxWidth: { md: "97vw" },
						},
					}}
				>
					<DocumentsTrackerDrawer
						handleCloseDrawer={closeUploadModal}
						handleOpenProcessingModal={openProcessingModal}
						handleCloseProcessingModal={closeProcessingModal}
						handleOpenSuccessModal={handleOpenSuccessModal}
						setProcessingText={setProcessingText}
						setSuccessText={setSuccessText}
						handleOpenCommonErrorModal={handleOpenCommonErrorModal}
						drawerData={{
							nextConditionsOptions: [selectedConditionOption],
							selectedConditionTypes: [selectedConditionOption.id],
							uploadHeaderText: `Uploading for condition ${selectedConditionOption.label}`,
							uploadSubHeaderText: selectedConditionOption.label,
							hideConditionSelection: true,
							uploadConditionId: conditionId,
							onUploadSuccess: handleUploadSuccess,
						}}
					/>
				</Drawer>

				<EnhancedConditionLinkExistingDrawer
					open={isLinkExistingOpen}
					handleCloseDrawer={closeLinkExistingModal}
					handleOpenProcessingModal={openProcessingModal}
					handleCloseProcessingModal={closeProcessingModal}
					handleOpenSuccessModal={handleOpenSuccessModal}
					setProcessingText={setProcessingText}
					setSuccessText={setSuccessText}
					handleOpenCommonErrorModal={handleOpenCommonErrorModal}
					drawerData={{
						loanId: resolvedLoanId,
						conditionId: conditionId,
						conditionTitle: selectedConditionOption.label,
						condition: selectedConditionOption,
						conditionType: selectedConditionOption.conditionType,
						supportingDocuments,
						onLinkExistingSuccess: handleLinkExistingSuccess,
						linkSuccessText: drawerData?.linkSuccessText,
					}}
				/>
			</Stack>
		</>
	);
};

export default EnhancedConditionDetailDrawer;
